<template>
  <div>
    <h2>Create Rate Card</h2>
    <el-form
      ref="form"
      :rules="rules"
      :model="form"
      size="small"
      label-width="auto"
    >
      <el-row :gutter="15">
        <el-col :md="16">
          <el-form-item
            label="Name"
            prop="name"
          >
            <el-input
              v-model="form.name"
              placeholder="Name"
              clearable
            />
          </el-form-item>
        </el-col>
        <el-col :md="8">
          <el-form-item
            label="Type"
            prop="type"
          >
            <el-select
              v-model="form.type"
              placeholder="Type"
              class="w-100"
            >
              <el-option
                label="Buy"
                value="buy"
              />
              <el-option
                label="Sell"
                value="sell"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col>
          <el-form-item
            label="Carrier"
            prop="carrier"
          >
            <el-select
              v-model="form.carrier"
              filterable
              remote
              placeholder="Enter name of carrier"
              class="w-100"
              value-key="id"
              :remote-method="remoteMethodCarrier"
              :loading="isLoadingCarriers"
            >
              <el-option
                v-for="item in carriers"
                :key="item.id"
                :label="item.name"
                :value="item"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <el-divider />
    <el-alert
      v-if="errorMsg"
      :title="errorMsg"
      type="error"
      class="mb-3"
    />
    <el-table
      :data="tableData"
      stripe
      size="mini"
      border
    >
      <el-table-column
        label="Original Column"
        :formatter="row => $_.get(row, 'original', '-')"
      />
      <el-table-column>
        <template #header>
          Mapping To
          <el-button
            size="mini"
            style="float: right;"
            @click.native="autoMapFields"
          >
            Auto Map
          </el-button>
        </template>
        <template slot-scope="scope">
          <el-select
            v-model="scope.row.mapping"
            placeholder="Mapping"
            size="small"
            class="w-100"
          >
            <el-option
              v-for="(field, index) in mappingFields"
              :key="index"
              :label="field.label"
              :value="field.value"
            >
              <span style="float: left">{{ field.label }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ field.value }}</span>
            </el-option>
          </el-select>
        </template>
      </el-table-column>
      <el-table-column
        width="400px"
        :formatter="row => row.records[showRecordIndex] ? row.records[showRecordIndex] : '-'"
      >
        <template #header>
          Preview Record {{ `(${showRecordIndex + 1} / ${parsed.length})` }}
          <el-button-group style="float: right; padding: 0;">
            <el-button
              size="mini"
              :disabled="showRecordIndex === 0"
              @click.native="showRecordIndex--"
            >
              Previous
            </el-button>
            <el-button
              size="mini"
              :disabled="showRecordIndex + 1 === parsed.length"
              @click.native="showRecordIndex++"
            >
              Next
            </el-button>
          </el-button-group>
        </template>
        <template slot-scope="scope">
          <template v-if="scope.row.original !== 'Breaks'">
            {{ scope.row.records[showRecordIndex] ? scope.row.records[showRecordIndex] : '-' }}
          </template>
          <el-table
            v-else
            :data="scope.row.records[showRecordIndex]"
            stripe
            size="mini"
            border
          >
            <el-table-column
              label="Type"
              prop="type"
            />
            <el-table-column
              label="Break"
              prop="break"
            />
            <el-table-column
              label="Basic"
              prop="basic"
            />
            <el-table-column
              label="Rate"
              prop="cost"
            />
          </el-table>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { papaparse } from '@/plugins/papaparse.js'
export default {
  props: {
    file: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      form: {},
      isLoadingCarriers: false,
      carriers: [],
      headers: [],
      parsed: [],
      tableData: [],
      showRecordIndex: 0,
      errorMsg: '',
      mappingFields: [
        { value: 'omit', label: 'Omit', description: 'Do not import this column', required: false },
        { value: 'service', label: 'Service', description: 'The service code (e.g. TXPX)', required: true },
        { value: 'zone_from', label: 'Zone From', description: 'The zone from code (e.g. SYD)', required: true },
        { value: 'zone_to', label: 'Zone To', description: 'The zone to code (e.g. MELB)', required: true },
        { value: 'accumulative', label: 'Accumulative', description: 'Whether it\'s accumulative (e.g. TRUE)', required: false },
        { value: 'reciprocal', label: 'Reciprocal', description: 'Whether it\'s reciprocal (e.g. TRUE)', required: false },
        { value: 'cubic', label: 'Cubic Rate', description: 'The cubic value (e.g. 250)', required: false },
        { value: 'min_charge', label: 'Minimum', description: 'The minimum charge value (e.g. 0)', required: false },
        { value: 'cube_limit', label: 'Cube Limit', description: 'The cubic limit (e.g. 0)', required: false },
        { value: 'weight_limit', label: 'KG Limit', description: 'The weight limit value (e.g. 0)', required: false },
        { value: 'round_weights_to', label: 'KG Rounding', description: 'Round weight to the nearest Kg (e.g. 0.5)', required: false },
        { value: 'price_breaks', label: 'Breaks', description: 'The breaks 2D array (e.g. At table of data)', required: true }
      ],
      pattern: ['Type', 'Break', 'Basic', 'Rate'],
      rules: {
        name: [
          { required: true, message: 'Name of the Rate Card is required', trigger: ['blur', 'change'] },
          { min: 5, max: 45, message: 'Length should be 5 to 45', trigger: ['blur', 'change'] }
        ],
        type: [
          { required: true, message: 'Type of the Rate Card is required', trigger: ['blur', 'change'] }
        ],
        carrier: [
          { required: true, message: 'Carrier of the Rate Card is required', trigger: 'change' }
        ]
      }
    }
  },
  created () {
    this.parseCsv()
  },
  methods: {
    parseCsv () {
      papaparse.parse(this.file.raw, {
        header: true,
        worker: true,
        skipEmptyLines: true,
        complete: res => {
          this.headers = res.meta.fields.filter(field => !this.pattern.some(p => field.startsWith(p)))
          const isMatchedPattern = !this.$_.isEmpty(res.meta.fields.filter(field => this.pattern.some(p => field.startsWith(p))))
          if (isMatchedPattern) {
            this.parsed = this.groupDataToBreaks(res.data)
            this.headers.push('Breaks')
          } else {
            this.errorMsg = 'Failed to find pattern in this file (Type, Break, Basic, Rate).'
            this.parsed = res.data
          }
          this.constructTableData()
        }
      })
    },
    remoteMethodCarrier (query) {
      this.isLoadingCarriers = true
      this.$sdk.carrier.list(builder => {
        builder.filters = { 'carrier_name': query }
      }).then(res => {
        this.carriers = res
      }).finally(() => { this.isLoadingCarriers = false })
    },
    groupDataToBreaks (data) {
      const newData = []
      data.map((d, index) => {
        let obj = {}
        this.headers.map(header => {
          obj[header] = d[header]
        })
        obj.Breaks = []

        for (let count = 1; count < 100; count++) {
          let isPatternExisted = this.pattern.every(p => {
            return !!Object.keys(d).find(key => key === `${p}${count}`)
          })
          if (isPatternExisted) {
            obj.Breaks.push({
              type: d[`Type${count}`],
              break: d[`Break${count}`],
              basic: d[`Basic${count}`],
              cost: d[`Rate${count}`]
            })
          }
        }
        newData.push(obj)
      })
      return newData
    },
    constructTableData () {
      const data = []
      this.headers.map(header => {
        data.push({
          original: header,
          mapping: 'omit',
          records: [...this.parsed.map(p => p[header])]
        })
      })
      this.tableData = data
    },
    autoMapFields () {
      this.tableData.map(td => {
        switch (td.original) {
          case 'Service':
            td.mapping = 'service'
            break
          case 'ZoneFrom':
            td.mapping = 'zone_from'
            break
          case 'ZoneTo':
            td.mapping = 'zone_to'
            break
          case 'Accumulative':
            td.mapping = 'accumulative'
            break
          case 'Reciprocal':
            td.mapping = 'reciprocal'
            break
          case 'CubicRate':
            td.mapping = 'cubic'
            break
          case 'Minimum':
            td.mapping = 'min_charge'
            break
          case 'Cube Limit':
            td.mapping = 'cube_limit'
            break
          case 'KG Limit':
            td.mapping = 'weight_limit'
            break
          case 'KG Rounding':
            td.mapping = 'round_weights_to'
            break
          case 'Breaks':
            td.mapping = 'price_breaks'
            break
        }
      })
    },
    validate () {
      this.$refs.form.validate((valid) => {
        if (valid) {
          const validated = this.mappingFields.filter(f => f.required).every(field => {
            const isExisted = this.tableData.some(td => {
              return td.mapping === field.value
            })
            if (!isExisted) {
              this.$message({ message: `Required Column Mapping Not Found: [${field.label}]`, type: 'error' })
            }
            return isExisted
          })

          if (validated) {
            this.$emit('submit', this.form, this.parsed, this.tableData)
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-divider--horizontal {
  margin-top: 10px;
  margin-bottom: 20px;
}
</style>
